/* =============================================================
   dealer-vip.js — Dealer VIP Program page interactions
   • Scroll progress bar
   • IntersectionObserver reveal (.dvp-reveal)
   • Hero stagger entrance
   • Counter animations
   • Tier progress fill
   • Pretext-pattern Canvas accordion height measurement
   ============================================================= */
(function () {
  'use strict';

  /* ── Pretext-pattern Canvas text measurement ── */
  var _ptxCache = new Map();

  function ptxMeasure(text, fontStr, containerWidth, lineHeightPx) {
    var key = fontStr + '|' + Math.round(containerWidth) + '|' + text.slice(0, 80);
    if (_ptxCache.has(key)) return _ptxCache.get(key);
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    ctx.font = fontStr;
    var words = text.split(/\s+/);
    var lines = 0; var current = '';
    for (var i = 0; i < words.length; i++) {
      var candidate = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(candidate).width > containerWidth && current) {
        lines++; current = words[i];
      } else { current = candidate; }
    }
    if (current) lines++;
    var result = { lineCount: Math.max(1, lines), height: Math.max(1, lines) * lineHeightPx };
    _ptxCache.set(key, result);
    return result;
  }

  function measureAccordionHeight(text, fontStr, containerWidth, lineHeightPx) {
    if (window.__bmwPretext && typeof window.__bmwPretext.measureBlockHeightCached === 'function') {
      return window.__bmwPretext.measureBlockHeightCached(text, fontStr, containerWidth, lineHeightPx);
    }
    return ptxMeasure(text, fontStr, containerWidth, lineHeightPx).height;
  }

  /* ── Progress bar ── */
  function initProgress() {
    var bar = document.querySelector('.dvp__progress');
    if (!bar) return;
    function update() {
      var doc = document.documentElement;
      var top = doc.scrollTop || document.body.scrollTop;
      var max = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
      bar.style.width = (max > 0 ? Math.min(100, (top / max) * 100) : 0).toFixed(2) + '%';
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── Scroll reveal ── */
  function initReveal() {
    var targets = document.querySelectorAll('.dvp-reveal');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ── Hero stagger ── */
  function initHeroStagger() {
    var els = document.querySelectorAll('.dvp__hero [data-stagger]');
    if (!els.length) return;
    els.forEach(function (el, i) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(14px)';
      el.style.transition = 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)';
      el.style.transitionDelay = (i * 0.1 + 0.1) + 's';
      setTimeout(function () { el.style.opacity = '1'; el.style.transform = 'none'; }, 60);
    });
  }

  /* ── Counter animation ── */
  function animateCounter(el, target, suffix, duration) {
    suffix = suffix || '';
    duration = duration || 900;
    var start = performance.now();
    function step(now) {
      var p = Math.min((now - start) / duration, 1);
      var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      var val = Math.round(eased * target);
      var display = target >= 1000 ? val.toLocaleString() : val;
      el.textContent = display + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        animateCounter(
          el,
          parseInt(el.getAttribute('data-counter'), 10),
          el.getAttribute('data-counter-suffix') || ''
        );
        io.unobserve(el);
      });
    }, { threshold: 0.3 });
    counters.forEach(function (el) { io.observe(el); });
  }

  /* ── Tier progress fill ── */
  function initTierProgress() {
    var fill = document.querySelector('.dvp__tier-progress-fill');
    if (!fill || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { fill.classList.add('animated'); io.unobserve(fill); }
      });
    }, { threshold: 0.5 });
    io.observe(fill);
  }

  /* ── Accordion with Pretext height measurement ── */
  function initAccordions() {
    var btns = document.querySelectorAll('.dvp__acc-btn');
    if (!btns.length) return;
    btns.forEach(function (btn) {
      var body = btn.nextElementSibling;
      var inner = body && body.querySelector('.dvp__acc-inner');
      if (!body || !inner) return;

      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () { body.style.height = '0'; });
          btn.setAttribute('aria-expanded', 'false');
        } else {
          var text = inner.textContent.replace(/\s+/g, ' ').trim();
          var fontStr = '300 13px Inter, system-ui, sans-serif';
          var lineHeight = 24; /* 13px × 1.8 ≈ 23.4 → 24 */
          var containerW = inner.getBoundingClientRect().width || inner.offsetWidth || 560;
          var measuredH = measureAccordionHeight(text, fontStr, containerW, lineHeight);
          var targetH = Math.ceil(measuredH * 1.2) + 28; /* top padding */
          body.style.height = targetH + 'px';
          btn.setAttribute('aria-expanded', 'true');
          body.addEventListener('transitionend', function onEnd() {
            if (btn.getAttribute('aria-expanded') === 'true') body.style.height = 'auto';
            body.removeEventListener('transitionend', onEnd);
          });
        }
      });
    });
  }

  /* ── Main ── */
  function init() {
    initProgress();
    initReveal();
    initHeroStagger();
    initCounters();
    initTierProgress();
    initAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  document.addEventListener('shopify:section:load', init);

})();
